import { CSSProperties, useMemo } from 'react';

import { Dimensions, Position } from '@vestico/utils';

import { CropHint, getBoundingPolyRect } from '@vestico/model';

export interface CropHintStylesProps {
  cropHint?: CropHint;
  mediaAspectRatio?: number;
  containerDimensions?: Dimensions;
  otherStyles?: CSSProperties;
}

export interface CropHintStyles {
  css: CSSProperties;
  position: Position | undefined;
}

export const useCropHintStyles = ({ mediaAspectRatio, cropHint, containerDimensions, otherStyles }: CropHintStylesProps) => {
  const position = useMemo(() => {
    if (!cropHint || !containerDimensions || !mediaAspectRatio) {
      return undefined;
    }

    const cropHintRect = getBoundingPolyRect(cropHint.boundingPoly);
    if (!cropHintRect) {
      return undefined;
    }

    const { x: cropX, y: cropY, width: cropWidth } = cropHintRect;
    const { width: containerWidth, height: containerHeight } = containerDimensions;

    const mediaWidth = Math.max(containerHeight * mediaAspectRatio, containerWidth);
    const mediaHeight = Math.max(containerWidth / mediaAspectRatio, containerHeight);

    const absoluteCropWidth = cropWidth * mediaWidth;
    const xContainerCenter = containerWidth * 0.5;

    const xPosAbsolute = cropX * mediaWidth;
    const yPosAbsolute = cropY * mediaHeight;

    // This is calculated based on the absolute position within the croprect and a relative shift from the center of the container
    // Needed for dynamic container dimensions that diverge from the crop rect aspect ratio
    const xPosAbsolutePreferred = Math.max(xPosAbsolute - (xContainerCenter - 0.5 * absoluteCropWidth), 0);

    // Shift back into frame if we go over bounds
    // We push the position back
    const xMax = mediaWidth - xPosAbsolutePreferred;
    const xContainerOverflow = Math.min(xMax - containerWidth, 0);
    const xFinalPos = xPosAbsolutePreferred + xContainerOverflow;

    // For y we do the same but don't calculate a position that takes the center of the container into consideration
    // This is because we preferably want the top of the crop rect to be always in frame (this is the area where there's faces most often!)
    const yMax = mediaHeight - yPosAbsolute;
    const yContainerOverflow = Math.min(yMax - containerHeight, 0);
    const yFinalPos = yPosAbsolute + yContainerOverflow;

    const position: Position = { x: -xFinalPos, y: -yFinalPos };
    return position;
  }, [cropHint, containerDimensions, mediaAspectRatio]);

  const css = useMemo(() => {
    const newCSS: CSSProperties = otherStyles ? { ...otherStyles } : {};
    if (!position) {
      return newCSS;
    }

    const { x, y } = position;
    newCSS.objectPosition = `${x}px ${y}px`;

    return newCSS;
  }, [otherStyles, position]);

  return { css, position };
};
